import { Control, FieldValues, Path, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const parseAmount = (value: string): number | null => {
  if (value === '') return null
  const numeric = parseFloat(value)
  return isNaN(numeric) ? null : numeric
}

// Per-currency price + optional free-from threshold, for delivery/payment
// method forms whose values contain `prices: EshopPriceRow[]`.
export function EshopPricesFields<T extends FieldValues>({
  control,
}: {
  control: Control<T>
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.prices',
  })

  const { fields } = useFieldArray({
    control,
    name: 'prices' as never,
  })

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[3rem_1fr_1fr] items-end gap-2 text-sm text-muted-foreground">
        <span />
        <span>{t('price')}</span>
        <span>{t('freeFrom')}</span>
      </div>
      {fields.map((row, index) => {
        const currency = (row as { currency?: string }).currency ?? ''

        return (
          <div
            key={row.id}
            className="grid grid-cols-[3rem_1fr_1fr] items-start gap-2"
          >
            <Label className="pt-2 font-mono text-sm">{currency}</Label>
            <FormField
              control={control}
              name={`prices.${index}.amount` as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(parseAmount(e.target.value) ?? 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`prices.${index}.freeFrom` as Path<T>}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      placeholder={t('freeFromPlaceholder')}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(parseAmount(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )
      })}
    </div>
  )
}
